import mockArtistsData from "../data/mockData/artistsData";
import { enrichArtistRow } from "./artistStoreDetails";
import mockLinkLandingPagesData from "../data/mockData/linkLandingPagesData";
import mockMusicLinksData from "../data/mockData/musicLinksData";
import mockMusicLinkUrlPartsData from "../data/mockData/musicLinkUrlPartsData";
import premadeColorsData from "../data/mockData/premadeColorsData";
import mockReleaseData from "../data/mockData/releasesData";
import mockReleaseDetailsData from "../data/mockData/releaseDetailsData";
import mockLabelsData from "../data/mockData/labelsData";
import { enrichLabelRow, validateLabelName } from "./labelDemoHelpers";
import {
  mockContributorsData,
  mockPublishersData,
  mockShareholdersData,
  mockTrackCostsData,
} from "../data/mockData/trackMetadataData";
import { mockTenantData, mockCurrentUserData, mockDeploymentCreditsData } from "../data/mockData/wlDemoData";
import {
  clearDemoTwoFactorStorage,
  generateDemoBackupCodes,
  getMergedDemoUser,
  patchDemoUserTwoFactor,
} from "./demoTwoFactorHelpers";
import { isBrowserSessionDemo, isDemoMode } from "./demoConfig";
import { clearAllDemoTrackBlobs } from "./demoTrackBlobStore";
import { getActiveDemoLandingSeedRows, getDemoLandingRows, removeDemoLandingRow, upsertDemoLandingRow } from "./demoLinkLandingSessionStore";
import { getDemoMusicLinkRows, removeDemoMusicLinkRow, upsertDemoMusicLinkRow } from "./demoMusicLinkSessionStore";
import { removeDemoMusicLinkUrlPartRow, upsertDemoMusicLinkUrlPartRow } from "./demoMusicLinkUrlPartsSessionStore";
import {
  buildMusicLinkListRowFromPayload,
  enrichMusicLinkDetail,
  enrichMusicLinkListRow,
  findMusicLinkByPublicParts,
  getMusicLinkById,
  getMusicLinkRows,
} from "./musicLinkDemoHelpers";
import {
  buildMusicLinkUrlPartRowFromPayload,
  enrichMusicLinkUrlPartRow,
  getMusicLinkUrlPartById,
  getMusicLinkUrlPartRows,
  validateMusicLinkUrlParts,
} from "./musicLinkUrlPartsDemoHelpers";
import { clearDemoAccountingSessionStorage } from "./demoAccountingSessionStore";
import { clearDemoAnnouncementsSessionStorage } from "./demoAnnouncementsSessionStore";
import { clearDemoInvoiceSessionStorage } from "./demoInvoiceSessionStore";
import { clearSubuserAccountingSessionStorage } from "./subuserAccountingSessionStore";
import { handleGenerateDemoInvoice } from "./invoiceDemoHelpers";
import {
  handleBulkMarkSubuserStatementsVisible,
  handleBulkSendSubuserStatementEmails,
  handleQueueSubuserReport,
  handleUpdateSubuserStatementStatus,
  handleUpdateSubuserStatementVisibility,
} from "./subuserStatementDemoHelpers";
import {
  handleCreateTaxRate,
  handleDeleteTaxRate,
  handleGenerateDemoSubuserInvoice,
  handleUpdateSubuserInvoiceStatus,
  handleUpdateTaxRate,
  handleUpdateTenantInvoiceConfig,
} from "./subuserInvoiceDemoHelpers";
import { normalizeUrl, paginateRows, getDetail, parseSubUserIdFromPath } from "./listQueryUtils";
import {
  appendArtistWishlist,
  clearArtistWishlistSessionStorage,
  getArtistWishlistById,
  getArtistWishlistRows,
  removeArtistWishlist,
} from "./artistWishlistDemoHelpers";
import {
  handleCreateSubUser,
  handlePatchSubUser,
  handleUpdateSubUser,
  getSubUserById,
  getSubUserRows,
  validateSubUserEmail,
  validateSubUserUsername,
} from "./subUserDemoHelpers";
import { applySubUserSeedToRows } from "../data/mockData/subUserSeedAssignments";
import {
  handleDemoAcrScanDeclaration,
  handleDemoPreScanDeclaration,
  handlePerformDemoPreScan,
  releaseExistsForDemoPreScan,
} from "./preScanDemoHelpers";
import {
  afterDemoReleaseStatusPatch,
  applyDueReleaseStatusTransitions,
  bootstrapReleaseTransitionTimers,
  enrichReleaseListRowFromStoredDetail,
  normalizeDemoReleaseStatusPatch,
  notifyReleaseStatusUpdated,
} from "./releaseDistributionDemoHelpers";
import { resolveDemoReleaseStoreUrls } from "./demoReleaseStoreUrlHelpers";
import { enrichReleaseEstimatedDelivery } from "./estimatedDeliveryHelpers";
import {
  clearDeliveryConfirmationsStorage,
  resolveDeliveryConfirmationDetail,
  resolveDeliveryConfirmationList,
} from "./deliveryConfirmationDemoHelpers";
import { resolveDemoReleaseEan, validateDemoEanUpc } from "./releaseEanDemoHelpers";
import {
  applyExistingUsersSubscriptionTransition,
  clearDemoStripeSubscriptionStorage,
  createDemoCheckoutSession,
  createDemoStripeProduct,
  deleteDemoStripeProduct,
  getDemoStripeConfig,
  getDemoStripeProduct,
  listDemoPublicPlans,
  listDemoStripeProducts,
  saveDemoStripeConfig,
  syncDemoStripeWebhook,
  updateDemoStripeProduct,
} from "./stripeSubscriptionDemoHelpers";

const API_URL = (process.env.REACT_APP_API_URL_BASE || "").replace(/\/+$/, "");
const PLATFORM_API_URL = (process.env.REACT_APP_PLATFORM_API_URL_BASE || "").replace(/\/+$/, "");

const STORAGE = {
  artists: "wl_bs_artists_extras",
  artistsDeleted: "wl_bs_artists_deleted",
  labels: "wl_bs_labels_extras",
  releases: "wl_bs_releases_extras",
  releaseDetails: "wl_bs_release_details_by_id",
  contributors: "wl_bs_contributors_extras",
  publishers: "wl_bs_publishers_extras",
  shareholders: "wl_bs_shareholders_extras",
  trackCosts: "wl_bs_track_costs_extras",
  musiclinks: "wl_bs_musiclinks_extras",
  landing: "wl_bs_landing_extras",
  landingDeleted: "wl_bs_landing_deleted",
  tenant: "wl_bs_tenant_overrides",
  user: "wl_bs_user_overrides",
  deploymentCredits: "wl_bs_deployment_credits_extras",
};
const LEGACY_LANDING_STORAGE_KEY = "wl_bs_landing_extras";

function normalizePayload(data) {
  if (data instanceof FormData) {
    const obj = {};
    data.forEach((value, key) => {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        if (!Array.isArray(obj[key])) {
          obj[key] = [obj[key]];
        }
        obj[key].push(value);
      } else {
        obj[key] = value;
      }
    });
    return obj;
  }
  if (data == null) {
    return {};
  }
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof data === "object" ? data : {};
}

function readJson(key, fallback) {
  if (typeof sessionStorage === "undefined") {
    return fallback;
  }
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) {
      return fallback;
    }
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(value));
}

function getBrowserSessionUser() {
  return getMergedDemoUser();
}

function writeBrowserSessionUserPatch(patch) {
  writeJson(STORAGE.user, { ...readJson(STORAGE.user, {}), ...(patch || {}) });
  return getMergedDemoUser();
}

export function resetBrowserSessionDemoStorage() {
  if (typeof sessionStorage === "undefined") {
    return;
  }
  clearDemoTwoFactorStorage();
  Object.values(STORAGE).forEach(k => sessionStorage.removeItem(k));
  clearDemoAccountingSessionStorage();
  clearDemoAnnouncementsSessionStorage();
  clearDemoInvoiceSessionStorage();
  clearSubuserAccountingSessionStorage();
  clearArtistWishlistSessionStorage();
  clearDeliveryConfirmationsStorage();
  clearDemoStripeSubscriptionStorage();
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(LEGACY_LANDING_STORAGE_KEY);
  }
  void clearAllDemoTrackBlobs();
}

/** Rows persisted in sessionStorage for browser-session demo (extras merged with static mock on GET). */
export function getBrowserSessionReleaseExtras() {
  if (!isBrowserSessionDemo() || typeof sessionStorage === "undefined") {
    return [];
  }
  return readJson(STORAGE.releases, []);
}

export function getBrowserSessionArtistExtras() {
  if (!isBrowserSessionDemo() || typeof sessionStorage === "undefined") {
    return [];
  }
  return readJson(STORAGE.artists, []);
}

function readDeletedArtistIds() {
  return new Set((readJson(STORAGE.artistsDeleted, []) || []).map(String));
}

function filterDeletedArtists(rows) {
  const del = readDeletedArtistIds();
  return (rows || []).filter(r => r && r.id != null && !del.has(String(r.id)));
}

function paginateSeededCollection(collection, rows, segments, queryParams) {
  const subUserIdFromPath = parseSubUserIdFromPath(segments);
  const seeded = applySubUserSeedToRows(collection, rows);
  return paginateRows(seeded, queryParams, { subUserIdFromPath });
}

function mergeById(baseRows, extras) {
  const normalizeLandingOverlay = (baseRow, extraRow) => {
    if (!baseRow) {
      return extraRow;
    }
    const merged = { ...baseRow, ...extraRow };
    if (
      Array.isArray(baseRow.social_links) &&
      baseRow.social_links.length > 0 &&
      (!Array.isArray(extraRow?.social_links) || extraRow.social_links.length === 0)
    ) {
      merged.social_links = baseRow.social_links;
    }
    if (
      Array.isArray(baseRow.featured_links) &&
      baseRow.featured_links.length > 0 &&
      (!Array.isArray(extraRow?.featured_links) || extraRow.featured_links.length === 0)
    ) {
      merged.featured_links = baseRow.featured_links;
    }
    return merged;
  };

  const map = new Map();
  (baseRows || []).forEach(r => {
    if (r && r.id != null) {
      map.set(String(r.id), r);
    }
  });
  (extras || []).forEach(r => {
    if (r && r.id != null) {
      const existing = map.get(String(r.id));
      map.set(String(r.id), normalizeLandingOverlay(existing, r));
    }
  });
  return Array.from(map.values());
}

function mergeCollectionRows(storageKey, seedRows) {
  return mergeById(seedRows || [], readJson(storageKey, []));
}

function appendCollectionRow(storageKey, seedRows, body) {
  const extras = readJson(storageKey, []);
  const id = nextId(seedRows || [], extras);
  const row = { ...(body || {}), id };
  extras.push(row);
  writeJson(storageKey, extras);
  return row;
}

function enrichReleaseListRowFromDetails(listRow, detailsById) {
  const merged = enrichReleaseListRowFromStoredDetail(listRow, detailsById);
  return enrichReleaseEstimatedDelivery(merged, getMergedDemoUser()?.sla_level);
}

function enrichReleaseDetailRow(listRow, detail) {
  const merged = {
    ...(listRow || {}),
    ...(detail || {}),
    store_urls: resolveDemoReleaseStoreUrls({ ...(listRow || {}), ...(detail || {}) }),
  };
  return enrichReleaseEstimatedDelivery(merged, getMergedDemoUser()?.sla_level);
}

function normalizeReleaseDetail(release, detail) {
  if (!detail) return null;
  const cloned = JSON.parse(JSON.stringify(detail));
  if (!Array.isArray(cloned.tracks)) {
    cloned.tracks = [];
  }
  const isSeededRelease = Number(release.id) <= 5;
  cloned.tracks = cloned.tracks.map((track, index) => {
    let next = track;
    if (!isSeededRelease || track.id == null || track.id === "") {
      const trackId = Number(release.id) * 1000 + (track.order != null ? Number(track.order) : index) + 1;
      next = { ...track, id: trackId };
    }
    return normalizeTrackQcDefaults(next);
  });
  return cloned;
}

function getReleaseDetailsMerged() {
  const extras = readJson(STORAGE.releases, []);
  const storedDetails = readJson(STORAGE.releaseDetails, {});
  const merged = mergeById(mockReleaseData, extras);
  return merged.map(release => {
    const sid = String(release.id);
    const detail = storedDetails[sid] || getDetail(mockReleaseDetailsData, release.id) || release;
    return normalizeReleaseDetail(release, detail);
  });
}

function getTrackById(id) {
  const sid = String(id);
  const details = getReleaseDetailsMerged();
  for (let i = 0; i < details.length; i += 1) {
    const tracks = Array.isArray(details[i]?.tracks) ? details[i].tracks : [];
    const hit = tracks.find(track => String(track.id) === sid);
    if (hit) {
      return hit;
    }
  }
  return null;
}

function nextId(baseRows, extras) {
  const merged = [...(baseRows || []), ...(extras || [])];
  return merged.reduce((m, r) => Math.max(m, Number(r.id) || 0), 0) + 1;
}

function normalizeTrackQcDefaults(track) {
  if (!track || typeof track !== "object") {
    return track;
  }
  return {
    ...track,
    ...(Object.prototype.hasOwnProperty.call(track, "qc_passed")
      ? {}
      : { qc_passed: true }),
    ...(track.qc_feedback != null ? {} : { qc_feedback: { results: {} } }),
    ...(track.edited_after_qc != null ? {} : { edited_after_qc: false }),
  };
}

function normalizeReleaseTracksQcDefaults(tracks) {
  if (!Array.isArray(tracks)) {
    return tracks;
  }
  return tracks.map(normalizeTrackQcDefaults);
}

function releaseDetailScaffold(id, listRow, body) {
  const base = mockReleaseDetailsData[0] || mockReleaseData[0] || {};
  const normalizedBody = {
    ...(body || {}),
    ...(Array.isArray(body?.tracks)
      ? { tracks: normalizeReleaseTracksQcDefaults(body.tracks) }
      : {}),
  };
  try {
    const clone = JSON.parse(JSON.stringify(base));
    const detail = {
      ...clone,
      ...listRow,
      ...normalizedBody,
      id,
    };
    if (Array.isArray(detail.tracks)) {
      detail.tracks = normalizeReleaseTracksQcDefaults(detail.tracks);
    }
    return detail;
  } catch {
    const detail = { ...listRow, ...normalizedBody, id };
    if (Array.isArray(detail.tracks)) {
      detail.tracks = normalizeReleaseTracksQcDefaults(detail.tracks);
    }
    return detail;
  }
}

function appendArtist(body) {
  const extras = readJson(STORAGE.artists, []);
  const id = nextId(mockArtistsData, extras);
  const row = enrichArtistRow({
    ...body,
    id,
    can_be_deleted: true,
    owner: body.owner || "John Doe",
    popularity: Number(body.popularity) || 0,
    followers: Number(body.followers) || 0,
  });
  extras.push(row);
  writeJson(STORAGE.artists, extras);
  return row;
}

function getOrCreateArtist(body) {
  const payload = body || {};
  const name = String(payload.name || "").trim();
  const spotifyId =
    payload.spotify_id != null && payload.spotify_id !== ""
      ? String(payload.spotify_id)
      : "";
  const deezerId =
    payload.deezer_id != null && payload.deezer_id !== ""
      ? String(payload.deezer_id)
      : "";

  const merged = filterDeletedArtists(
    mergeById(mockArtistsData, readJson(STORAGE.artists, [])),
  );

  const matchesArtist = row => {
    if (spotifyId) {
      const rowSpotifyId = String(
        row.spotify_identifier || row.spotify_details?.id || "",
      );
      if (rowSpotifyId && rowSpotifyId === spotifyId) {
        return true;
      }
    }
    if (deezerId) {
      const rowDeezerId = String(
        row.deezer_identifier || row.deezer_details?.id || "",
      );
      if (rowDeezerId && rowDeezerId === deezerId) {
        return true;
      }
    }
    if (name) {
      return String(row.name || "").trim().toLowerCase() === name.toLowerCase();
    }
    return false;
  };

  const existing = merged.find(matchesArtist);
  if (existing) {
    return enrichArtistRow(existing);
  }

  return appendArtist({
    name: name || "Unknown Artist",
    ...(spotifyId && { spotify_identifier: spotifyId }),
    ...(deezerId && { deezer_identifier: deezerId }),
  });
}

function patchArtistRecord(id, body) {
  const extras = readJson(STORAGE.artists, []);
  const merged = mergeById(mockArtistsData, extras);
  const base = merged.find(r => String(r.id) === String(id)) || {};
  const row = enrichArtistRow({
    ...base,
    ...(body || {}),
    id: Number(id) || id,
  });
  const idx = extras.findIndex(r => String(r.id) === String(id));
  if (idx >= 0) {
    extras[idx] = { ...extras[idx], ...row };
  } else {
    extras.push(row);
  }
  writeJson(STORAGE.artists, extras);
  return row;
}

function removeArtistRecord(id) {
  const sid = String(id);
  let extras = readJson(STORAGE.artists, []);
  extras = extras.filter(r => String(r.id) !== sid);
  writeJson(STORAGE.artists, extras);
  const deleted = readJson(STORAGE.artistsDeleted, []);
  if (!deleted.map(String).includes(sid)) {
    deleted.push(Number(id) || id);
    writeJson(STORAGE.artistsDeleted, deleted);
  }
}

function appendLabel(body) {
  const extras = readJson(STORAGE.labels, []);
  const id = nextId(mockLabelsData, extras);
  const row = enrichLabelRow({
    ...body,
    id,
    name: body.name || `Label ${id}`,
    company: body.company || "ABC Company GmbH",
    contract_received: false,
    information_accepted: false,
    label_approved: false,
    owner: body.owner || "John Doe",
    promo_graphic: body.promo_graphic || {},
  });
  extras.push(row);
  writeJson(STORAGE.labels, extras);
  return row;
}

function patchLabelRecord(id, body) {
  const extras = readJson(STORAGE.labels, []);
  const merged = mergeById(mockLabelsData, extras);
  const base = merged.find(r => String(r.id) === String(id)) || {};
  const patch = { ...(body || {}) };
  if (!Object.prototype.hasOwnProperty.call(patch, "logo")) {
    delete patch.logo;
  }
  if (!Object.prototype.hasOwnProperty.call(patch, "promo_graphic")) {
    delete patch.promo_graphic;
  }
  const row = enrichLabelRow({
    ...base,
    ...patch,
    id: Number(id) || id,
  });
  const idx = extras.findIndex(r => String(r.id) === String(id));
  if (idx >= 0) {
    extras[idx] = { ...extras[idx], ...row };
  } else {
    extras.push(row);
  }
  writeJson(STORAGE.labels, extras);
  return row;
}

function resolveArtistEntryDisplayName(entry) {
  if (!entry || typeof entry !== "object") return "";
  if (typeof entry.artist === "object" && entry.artist?.name) {
    return String(entry.artist.name).trim();
  }
  if (entry.name) return String(entry.name).trim();
  if (entry.artist_name) return String(entry.artist_name).trim();
  const artistId = entry.artist;
  if (artistId != null && artistId !== "") {
    const merged = mergeById(mockArtistsData, readJson(STORAGE.artists, []));
    const artist = merged.find(r => String(r.id) === String(artistId));
    if (artist?.name) return String(artist.name).trim();
  }
  return "";
}

function resolveReleaseArtistsDisplay(artists) {
  if (!Array.isArray(artists) || artists.length === 0) return "";
  const mainEntries = artists.filter(a => !a.kind || a.kind === "main");
  const entries = mainEntries.length ? mainEntries : artists;
  const names = [...new Set(entries.map(resolveArtistEntryDisplayName).filter(Boolean))];
  return names.join(", ");
}

function appendRelease(body) {
  const extras = readJson(STORAGE.releases, []);
  const details = readJson(STORAGE.releaseDetails, {});
  const id = nextId(mockReleaseData, extras);
  const payload = { ...(body || {}) };
  const eanResolution = resolveDemoReleaseEan(payload);
  payload.ean = eanResolution.ean;
  payload.generate_ean = eanResolution.generate_ean;
  const artistsDisplay = resolveReleaseArtistsDisplay(payload.artists);
  const listRow = {
    id,
    name: payload.name || artistsDisplay || `Browser release ${id}`,
    created: new Date().toISOString(),
    label: typeof payload.label === "object" && payload.label ? payload.label.label : payload.label || "",
    catalogue_number: payload.catalogue_number || "",
    artwork: payload.artwork || null,
    get_artists: artistsDisplay || "Various",
    status: "offline",
    ean: payload.ean || "",
    official_date: payload.official_date || "",
    original_date: payload.original_date || payload.official_date || "",
    exclusive_date: payload.exclusive_shop || null,
    get_mixes_number: Array.isArray(payload.tracks) ? payload.tracks.length : 0,
    all_tracks_ready: false,
    store_urls: [],
    qc_passed: true,
    qc_feedback: { results: {} },
    edited_after_qc: false,
  };
  extras.push(listRow);
  writeJson(STORAGE.releases, extras);
  details[String(id)] = releaseDetailScaffold(id, listRow, payload);
  writeJson(STORAGE.releaseDetails, details);
  return { id, ...listRow };
}

function patchReleaseRecord(id, body) {
  const normalizedBody = normalizeDemoReleaseStatusPatch(id, normalizePayload(body) || {});

  const shouldGenerateEan =
    normalizedBody.generate_ean === true ||
    normalizedBody.generate_ean === "true" ||
    normalizedBody.generate_ean === 1 ||
    normalizedBody.generate_ean === "1";

  if (shouldGenerateEan) {
    const eanResolution = resolveDemoReleaseEan({ generate_ean: true });
    normalizedBody.ean = eanResolution.ean;
    normalizedBody.generate_ean = true;
  } else if (Object.prototype.hasOwnProperty.call(normalizedBody, "generate_ean")) {
    normalizedBody.generate_ean = false;
  }

  const sid = String(id);
  const extras = readJson(STORAGE.releases, []);
  const details = readJson(STORAGE.releaseDetails, {});
  const mergedList = mergeById(mockReleaseData, extras);

  const baseListRow = mergedList.find(r => String(r.id) === sid) || {};
  const baseDetail = details[sid] || getDetail(mergeById(mockReleaseDetailsData, []), sid) || {};

  const sourceTracks = Array.isArray(normalizedBody?.tracks)
    ? normalizedBody.tracks
    : (Array.isArray(baseDetail?.tracks) ? baseDetail.tracks : []);

  const firstTrack = sourceTracks[0] || null;
  const firstArtistName =
    resolveReleaseArtistsDisplay(normalizedBody?.artists) ||
    (firstTrack && Array.isArray(firstTrack.artists)
      ? resolveReleaseArtistsDisplay(firstTrack.artists)
      : "") ||
    baseListRow.get_artists ||
    baseDetail.get_artists ||
    "";

  const listRowPatch = {
    ...baseListRow,
    ...(normalizedBody?.name !== undefined ? { name: normalizedBody.name } : {}),
    ...(normalizedBody?.catalogue_number !== undefined ? { catalogue_number: normalizedBody.catalogue_number } : {}),
    ...(normalizedBody?.ean !== undefined ? { ean: normalizedBody.ean } : {}),
    ...(normalizedBody?.official_date !== undefined ? { official_date: normalizedBody.official_date } : {}),
    ...(normalizedBody?.original_date !== undefined ? { original_date: normalizedBody.original_date } : {}),
    ...(normalizedBody?.exclusive_shop !== undefined ? { exclusive_date: normalizedBody.exclusive_shop } : {}),
    ...(normalizedBody?.status !== undefined ? { status: normalizedBody.status } : {}),
    ...(normalizedBody?.all_tracks_ready !== undefined ? { all_tracks_ready: normalizedBody.all_tracks_ready } : {}),
    ...(normalizedBody?.has_delivery_confirmations !== undefined
      ? { has_delivery_confirmations: normalizedBody.has_delivery_confirmations }
      : {}),
    ...(normalizedBody?.tracks !== undefined ? { get_mixes_number: sourceTracks.length } : {}),
    ...(normalizedBody?.tracks !== undefined && normalizedBody?.all_tracks_ready === undefined
      ? { all_tracks_ready: sourceTracks.length > 0 }
      : {}),
    ...(firstArtistName ? { get_artists: firstArtistName } : {}),
  };

  const releaseContext = {
    ...baseListRow,
    ...baseDetail,
    ...listRowPatch,
    id: Number(id) || id,
  };

  listRowPatch.store_urls = Array.isArray(normalizedBody?.store_urls)
    ? normalizedBody.store_urls
    : resolveDemoReleaseStoreUrls(releaseContext);

  const enrichedListRow = enrichReleaseEstimatedDelivery(listRowPatch, getMergedDemoUser()?.sla_level);

  const detailPatch = {
    ...baseDetail,
    ...normalizedBody,
    id: Number(id) || id,
    store_urls: listRowPatch.store_urls,
    ...(enrichedListRow.estimated_delivery_time != null
      ? { estimated_delivery_time: enrichedListRow.estimated_delivery_time }
      : {}),
  };

  details[sid] = detailPatch;
  writeJson(STORAGE.releaseDetails, details);

  const listExtraIndex = extras.findIndex(r => String(r.id) === sid);
  if (listExtraIndex >= 0) {
    extras[listExtraIndex] = enrichedListRow;
  } else {
    extras.push(enrichedListRow);
  }
  writeJson(STORAGE.releases, extras);

  if (normalizedBody.status != null) {
    notifyReleaseStatusUpdated();
    afterDemoReleaseStatusPatch(id, normalizedBody.status, patchReleaseRecord);
  }

  return detailPatch;
}

function appendMusicLink(body) {
  const extras = getDemoMusicLinkRows();
  const id = nextId(mockMusicLinksData, extras);
  const row = buildMusicLinkListRowFromPayload(body, id);
  upsertDemoMusicLinkRow(row);
  return row;
}

function appendMusicLinkUrlPart(body) {
  const payload = normalizePayload(body);
  const rows = getMusicLinkUrlPartRows();
  const id = nextId(mockMusicLinkUrlPartsData, rows);
  const row = buildMusicLinkUrlPartRowFromPayload({ ...payload, owner: "John Doe" }, id);
  upsertDemoMusicLinkUrlPartRow(row);
  return row;
}

function patchMusicLinkUrlPartRecord(id, body) {
  const payload = normalizePayload(body);
  const merged = getMusicLinkUrlPartRows();
  const baseRow = merged.find(r => String(r.id) === String(id)) || {};
  const row = buildMusicLinkUrlPartRowFromPayload(
    { ...baseRow, ...payload, id: Number(id) || id },
    id,
  );
  upsertDemoMusicLinkUrlPartRow(row);
  return row;
}

function patchMusicLinkRecord(id, body) {
  const payload = normalizePayload(body);
  const sid = String(id);
  const merged = getMusicLinkRows();
  const baseRow = merged.find(r => String(r.id) === sid) || {};
  const row = buildMusicLinkListRowFromPayload(
    { ...enrichMusicLinkDetail(baseRow), ...payload, id: Number(id) || id },
    id,
  );
  upsertDemoMusicLinkRow(row);
  return row;
}

function appendLanding(body) {
  const payload = normalizePayload(body);
  const extras = getDemoLandingRows();
  const id = nextId(mockLinkLandingPagesData, extras);
  const back = payload.back_url || `demo-${id}`;
  const origin =
    typeof window !== "undefined" && window.location && window.location.origin
      ? window.location.origin
      : "http://localhost:3000";
  const preset = premadeColorsData.find(item => String(item.id) === String(payload?.premade_color));
  const custom = payload?.custom_color && typeof payload.custom_color === "object" ? payload.custom_color : null;
  const colorFromPayload =
    Number(payload?.color_scheme) === 2 && custom ? custom : (preset || null);

  const row = {
    ...payload,
    id,
    title: payload.title || `Landing ${id}`,
    short_description: payload.short_description || "",
    logo: payload.logo || "",
    background_image: payload.background_image || null,
    background_blurred: Boolean(payload.background_blurred),
    blur_strength: Number(payload.blur_strength) || 6,
    full_url: `${origin}/b/${back}/`,
    back_url: back,
    domain: payload.domain || null,
    layout: Number(payload.layout) || 1,
    font: Number(payload.font) || 26,
    button_corners: Number(payload.button_corners) || 1,
    button_style: Number(payload.button_style) || 1,
    social_icon_placement: Number(payload.social_icon_placement) || 1,
    social_icon_color: Number(payload.social_icon_color) || 1,
    color_scheme: Number(payload.color_scheme) || 1,
    premade_color: payload.premade_color || null,
    custom_color: payload.custom_color || null,
    header_color_scheme: colorFromPayload?.header_color_scheme || payload.header_color_scheme || 1,
    body_color_scheme: colorFromPayload?.body_color_scheme || payload.body_color_scheme || 1,
    header_gradient: colorFromPayload?.header_gradient || payload.header_gradient || null,
    body_gradient: colorFromPayload?.body_gradient || payload.body_gradient || null,
    header_color: colorFromPayload?.header_color || payload.header_color || "#ffffff",
    body_color: colorFromPayload?.body_color || payload.body_color || "#ffffff",
    background_color: colorFromPayload?.background_color || payload.background_color || "#ffffff",
    title_color: colorFromPayload?.title_color || payload.title_color || "#000000",
    description_color: colorFromPayload?.description_color || payload.description_color || "#000000",
    button_color: colorFromPayload?.button_color || payload.button_color || "#111111",
    button_text_color: colorFromPayload?.button_text_color || payload.button_text_color || "#ffffff",
    social_links: Array.isArray(payload.social_links) ? payload.social_links : [],
    featured_links: Array.isArray(payload.featured_links) ? payload.featured_links : [],
    created: new Date().toISOString(),
  };
  upsertDemoLandingRow(row);
  return row;
}

function patchLandingRecord(id, body) {
  const payload = normalizePayload(body);
  const sid = String(id);
  const origin =
    typeof window !== "undefined" && window.location && window.location.origin
      ? window.location.origin
      : "http://localhost:3000";
  const merged = getLandingRows();
  const baseRow = merged.find(r => String(r.id) === sid) || {};
  const nextBody = { ...baseRow, ...payload };
  const preset = premadeColorsData.find(item => String(item.id) === String(nextBody?.premade_color));
  const custom = nextBody?.custom_color && typeof nextBody.custom_color === "object" ? nextBody.custom_color : null;
  const colorFromPayload =
    Number(nextBody?.color_scheme) === 2 && custom ? custom : (preset || null);
  const back = nextBody.back_url || baseRow.back_url || `demo-${id}`;

  const row = {
    ...baseRow,
    ...nextBody,
    id: Number(id) || id,
    back_url: back,
    full_url: `${origin}/b/${back}/`,
    color_scheme: Number(nextBody.color_scheme) || Number(baseRow.color_scheme) || 1,
    header_color_scheme: colorFromPayload?.header_color_scheme || nextBody.header_color_scheme || baseRow.header_color_scheme || 1,
    body_color_scheme: colorFromPayload?.body_color_scheme || nextBody.body_color_scheme || baseRow.body_color_scheme || 1,
    header_gradient: colorFromPayload?.header_gradient || nextBody.header_gradient || baseRow.header_gradient || null,
    body_gradient: colorFromPayload?.body_gradient || nextBody.body_gradient || baseRow.body_gradient || null,
    header_color: colorFromPayload?.header_color || nextBody.header_color || baseRow.header_color || "#ffffff",
    body_color: colorFromPayload?.body_color || nextBody.body_color || baseRow.body_color || "#ffffff",
    background_color: colorFromPayload?.background_color || nextBody.background_color || baseRow.background_color || "#ffffff",
    title_color: colorFromPayload?.title_color || nextBody.title_color || baseRow.title_color || "#000000",
    description_color: colorFromPayload?.description_color || nextBody.description_color || baseRow.description_color || "#000000",
    button_color: colorFromPayload?.button_color || nextBody.button_color || baseRow.button_color || "#111111",
    button_text_color: colorFromPayload?.button_text_color || nextBody.button_text_color || baseRow.button_text_color || "#ffffff",
    social_links: Array.isArray(nextBody.social_links) ? nextBody.social_links : (baseRow.social_links || []),
    featured_links: Array.isArray(nextBody.featured_links) ? nextBody.featured_links : (baseRow.featured_links || []),
  };

  upsertDemoLandingRow(row);
  return row;
}

function getLandingRows() {
  return mergeById(getActiveDemoLandingSeedRows(mockLinkLandingPagesData), getDemoLandingRows());
}

function appendContributor(body) {
  return appendCollectionRow(STORAGE.contributors, mockContributorsData, body);
}

function appendPublisher(body) {
  return appendCollectionRow(STORAGE.publishers, mockPublishersData, body);
}

function appendShareholder(body) {
  return appendCollectionRow(STORAGE.shareholders, mockShareholdersData, body);
}

function appendTrackCost(body) {
  return appendCollectionRow(STORAGE.trackCosts, mockTrackCostsData, body);
}

export function getDeploymentCreditRows() {
  return mergeById(mockDeploymentCreditsData, readJson(STORAGE.deploymentCredits, []));
}

function appendDeploymentCreditUsage({ description, quantity = 1, balance, kind = "debit" }) {
  const extras = readJson(STORAGE.deploymentCredits, []);
  const id = nextId(mockDeploymentCreditsData, extras);
  const row = {
    id,
    quantity,
    description,
    kind,
    balance,
    created: new Date().toISOString(),
    stripe_receipt_url: "",
  };
  extras.push(row);
  writeJson(STORAGE.deploymentCredits, extras);
  return row;
}

function mergeTenantFromBranding(body) {
  const cur = readJson(STORAGE.tenant, {});
  const next = { ...cur };
  Object.keys(body || {}).forEach(k => {
    if (body[k] === undefined) {
      return;
    }
    if (
      k === "branding" &&
      body.branding &&
      typeof body.branding === "object" &&
      !Array.isArray(body.branding)
    ) {
      next.branding = { ...(cur.branding || {}), ...body.branding };
    } else {
      next[k] = body[k];
    }
  });
  writeJson(STORAGE.tenant, next);

  if (
    body?.subscriptions_enabled &&
    body?.existing_users_transition
  ) {
    applyExistingUsersSubscriptionTransition(body.existing_users_transition);
  }

  const nowIso = new Date().toISOString();
  const user = getBrowserSessionUser();
  const currentCredits = Number(user.deployment_credits) || 0;
  const nextCredits = currentCredits > 0 ? currentCredits - 1 : 0;
  writeBrowserSessionUserPatch({
    deployment_credits: nextCredits,
    last_branding_updated: nowIso,
  });
  appendDeploymentCreditUsage({
    description: "Branding configuration update",
    quantity: 1,
    balance: nextCredits,
  });
  return { status: "ok" };
}

/** Demo tenant merged from seed + session overrides (branding nested). */
export function getMergedWlDemoTenantSnapshot() {
  const overrides = readJson(STORAGE.tenant, {});
  return {
    ...mockTenantData,
    ...overrides,
    branding: {
      ...(mockTenantData.branding || {}),
      ...(overrides.branding || {}),
    },
  };
}

export function getBrowserSessionGetResponse(url) {
  if (!isBrowserSessionDemo()) {
    return undefined;
  }

  const { path, queryParams } = normalizeUrl(url);

  if (path === "tenants" || path === "tenants/") {
    const tenant = getMergedWlDemoTenantSnapshot();
    return { count: 1, results: [tenant] };
  }

  if (path === "tenant-stripe-config" || path === "tenant-stripe-config/") {
    return getDemoStripeConfig();
  }

  if (path === "tenant-stripe-products" || path === "tenant-stripe-products/") {
    const products = listDemoStripeProducts();
    return { count: products.length, results: products };
  }

  if (path.startsWith("tenant-stripe-products/")) {
    const segments = path.split("/").filter(Boolean);
    const productId = segments[1];
    if (productId && /^\d+$/.test(productId)) {
      return getDemoStripeProduct(productId) || { detail: "Not found." };
    }
  }

  if (path === "tenant-stripe-plans" || path === "tenant-stripe-plans/") {
    const plans = listDemoPublicPlans();
    return { count: plans.length, results: plans };
  }

  if (path === "users" || path.startsWith("users/")) {
    const user = getBrowserSessionUser();
    if (path === "users" || path === "users/") {
      return { count: 1, results: [user] };
    }
    const segments = path.split("/").filter(Boolean);
    if (segments.length >= 2 && /^\d+$/.test(segments[1])) {
      if (String(user.id) === String(segments[1])) {
        return user;
      }
      return { detail: "Not found." };
    }
    return undefined;
  }

  if (path === "releases/get-release-details" || path.startsWith("releases/get-release-details")) {
    return undefined;
  }

  if (
    path === "get-release-details" ||
    path.startsWith("get-release-details")
  ) {
    return undefined;
  }

  if (path.startsWith("artists/spotify-artist-details")) {
    return undefined;
  }

  const segments = path.split("/").filter(Boolean);
  const col = segments[0];

  if (col === "ddex-delivery-confirmations") {
    const detailId = segments.length >= 2 ? segments[1] : "";
    if (detailId && /^\d+$/.test(detailId)) {
      const detail = resolveDeliveryConfirmationDetail(detailId);
      return detail || { detail: "Not found." };
    }
    return resolveDeliveryConfirmationList(queryParams);
  }

  if (col === "artists") {
    const mergedAll = mergeById(mockArtistsData, readJson(STORAGE.artists, []));
    const merged = filterDeletedArtists(mergedAll);
    const detailId = segments.length >= 2 ? segments[1] : "";
    const isSubUserScopedList = parseSubUserIdFromPath(segments) != null;
    const isNumericDetail =
      segments.length === 2 && detailId && detailId !== "spotify-artist-details" && /^\d+$/.test(detailId);
    if (isNumericDetail && !isSubUserScopedList) {
      const row = merged.find(r => String(r.id) === String(detailId));
      return row ? enrichArtistRow(row) : { detail: "Not found." };
    }
    return paginateSeededCollection("artists", merged, segments, queryParams);
  }

  if (col === "artist-wishlist") {
    const rows = getArtistWishlistRows();
    const detailId = segments.length >= 2 ? segments[1] : "";
    const isSubUserScopedList = parseSubUserIdFromPath(segments) != null;
    const isNumericDetail = segments.length === 2 && detailId && /^\d+$/.test(detailId);
    if (isNumericDetail && !isSubUserScopedList) {
      const row = getArtistWishlistById(detailId);
      return row || { detail: "Not found." };
    }
    return paginateSeededCollection("artist-wishlist", rows, segments, queryParams);
  }

  if (col === "labels") {
    const merged = mergeById(mockLabelsData, readJson(STORAGE.labels, [])).map(enrichLabelRow);
    const detailId = segments.length >= 2 ? segments[1] : "";
    const isSubUserScopedList = parseSubUserIdFromPath(segments) != null;
    const isNumericDetail = segments.length === 2 && detailId && /^\d+$/.test(detailId);
    if (isNumericDetail && !isSubUserScopedList) {
      const row = merged.find(r => String(r.id) === String(detailId));
      return row || { detail: "Not found." };
    }
    return paginateSeededCollection("labels", merged, segments, queryParams);
  }

  if (col === "releases") {
    applyDueReleaseStatusTransitions(patchReleaseRecord);
    bootstrapReleaseTransitionTimers(patchReleaseRecord);
    const extras = readJson(STORAGE.releases, []);
    const details = readJson(STORAGE.releaseDetails, {});
    const isSubUserScopedList = parseSubUserIdFromPath(segments) != null;
    if (segments.length >= 2 && segments[1] && !isSubUserScopedList) {
      const id = segments[1];
      const sid = String(id);
      const listRows = mergeById(mockReleaseData, extras);
      const listRow = getDetail(listRows, id) || {};
      let detail;
      if (details[sid]) {
        detail = details[sid];
      } else {
        const seedDetail = getDetail(mockReleaseDetailsData, id);
        if (seedDetail) {
          detail = { ...seedDetail, ...listRow, id: seedDetail.id };
        } else {
          detail = listRow;
        }
      }
      return normalizeReleaseDetail(
        { id },
        enrichReleaseDetailRow(listRow, detail)
      );
    }
    const merged = mergeById(mockReleaseData, extras).map(row =>
      enrichReleaseListRowFromDetails(row, details)
    );
    return paginateSeededCollection("releases", merged, segments, queryParams);
  }

  if (col === "tracks") {
    if (segments.length >= 2 && segments[1]) {
      return getTrackById(segments[1]) || { detail: "Not found." };
    }
    return { count: 0, results: [] };
  }

  if (col === "sub-users") {
    const rows = getSubUserRows();
    const detailId = segments.length >= 2 ? segments[1] : "";
    const isSubUserScopedList = parseSubUserIdFromPath(segments) != null;
    const isNumericDetail = segments.length === 2 && detailId && /^\d+$/.test(detailId);
    if (isNumericDetail && !isSubUserScopedList) {
      const row = getSubUserById(detailId);
      return row || { detail: "Not found." };
    }
    return paginateSeededCollection("sub-users", rows, segments, queryParams);
  }

  const collectionConfig = {
    contributors: { key: STORAGE.contributors, seed: mockContributorsData },
    publishers: { key: STORAGE.publishers, seed: mockPublishersData },
    shareholders: { key: STORAGE.shareholders, seed: mockShareholdersData },
    "track-costs": { key: STORAGE.trackCosts, seed: mockTrackCostsData },
  };
  const cfg = collectionConfig[col];
  if (cfg) {
    const rows = mergeCollectionRows(cfg.key, cfg.seed);
    const detailId = segments.length >= 2 ? segments[1] : "";
    const isSubUserScopedList = parseSubUserIdFromPath(segments) != null;
    const isNumericDetail = segments.length === 2 && detailId && /^\d+$/.test(detailId);
    if (isNumericDetail && !isSubUserScopedList) {
      return getDetail(rows, detailId);
    }
    return paginateSeededCollection(col, rows, segments, queryParams);
  }

  if (col === "musiclinks") {
    if (segments[1] === "stats") {
      return undefined;
    }
    const isSubUserScopedList = parseSubUserIdFromPath(segments) != null;
    if (segments.length >= 2 && segments[1] && !isSubUserScopedList) {
      return getMusicLinkById(segments[1]);
    }
    const merged = getMusicLinkRows().map(enrichMusicLinkListRow);
    return paginateSeededCollection("musiclinks", merged, segments, queryParams);
  }

  if (col === "musiclinks_urlparts") {
    if (path.includes("validate-urlparts")) {
      return undefined;
    }
    const isSubUserScopedList = parseSubUserIdFromPath(segments) != null;
    if (segments.length >= 2 && segments[1] && !isSubUserScopedList) {
      return getMusicLinkUrlPartById(segments[1]);
    }
    const merged = getMusicLinkUrlPartRows().map(enrichMusicLinkUrlPartRow);
    return paginateSeededCollection("musiclinks_urlparts", merged, segments, queryParams);
  }

  if (path === "musiclink-landingpage") {
    const urlPart1 = queryParams.get("url_part1");
    const urlPart2 = queryParams.get("url_part2");
    const row = findMusicLinkByPublicParts(urlPart1, urlPart2);
    if (!row) {
      return { detail: "Not found." };
    }
    return enrichMusicLinkDetail(row);
  }

  if (col === "deployment-credits") {
    const rows = getDeploymentCreditRows();
    return paginateSeededCollection("deployment-credits", rows, segments, queryParams);
  }

  if (col === "link-landingpages") {
    if (segments[1] === "available-domains") {
      const origin =
        typeof window !== "undefined" && window.location && window.location.origin
          ? window.location.origin
          : "http://localhost:3000";
      return {
        count: 1,
        results: [
          {
            id: 1,
            domain_url: origin,
            premium: false,
          },
        ],
      };
    }
    if (
      segments[1] === "stats" ||
      path.includes("validate-back-url")
    ) {
      return undefined;
    }
    const isSubUserScopedList = parseSubUserIdFromPath(segments) != null;
    if (segments.length >= 2 && segments[1] && !isSubUserScopedList) {
      const merged = getLandingRows();
      return getDetail(merged, segments[1]);
    }
    const merged = getLandingRows();
    return paginateSeededCollection("link-landingpages", merged, segments, queryParams);
  }

  if (path === "link-landingpage") {
    const backUrl = queryParams.get("back_url");
    if (!backUrl) {
      return { detail: "Not found." };
    }
    const merged = getLandingRows();
    const row = merged.find(r => String(r.back_url).toLowerCase() === String(backUrl).toLowerCase());
    if (!row) {
      return { detail: "Not found." };
    }
    return {
      layout: row.layout || 1,
      font: row.font || 26,
      social_icon_placement: row.social_icon_placement || 1,
      social_icon_color: row.social_icon_color || 1,
      button_corners: row.button_corners || 1,
      button_style: row.button_style || 1,
      color_scheme: row.color_scheme || 1,
      header_color_scheme: row.header_color_scheme || 1,
      body_color_scheme: row.body_color_scheme || 1,
      header_color: row.header_color || "#ffffff",
      body_color: row.body_color || "#ffffff",
      header_gradient: row.header_gradient || null,
      body_gradient: row.body_gradient || null,
      title_color: row.title_color || "#000000",
      description_color: row.description_color || "#000000",
      button_color: row.button_color || "#111111",
      button_text_color: row.button_text_color || "#ffffff",
      background_color: row.background_color || "#ffffff",
      social_links: Array.isArray(row.social_links) ? row.social_links : [],
      featured_links: Array.isArray(row.featured_links) ? row.featured_links : [],
      ...row,
      logo:
        row.logo && typeof row.logo === "object"
          ? row.logo
          : row.logo
            ? { thumb_small: row.logo, full_size: row.logo }
            : null,
      background_image:
        row.background_image && typeof row.background_image === "object"
          ? row.background_image
          : row.background_image
            ? { thumb_small: row.background_image, full_size: row.background_image }
            : null,
    };
  }

  return undefined;
}

export function relativeApiPath(fullUrl) {
  if (!fullUrl) {
    return "";
  }
  const u = String(fullUrl);
  const bases = [API_URL, PLATFORM_API_URL].filter(Boolean);
  const base = bases.find(b => u.startsWith(b));
  if (!base) {
    return "";
  }
  return u.slice(base.length).replace(/^\/+/, "");
}

/** Mutating POST handlers for white-label demo (no real API). */
export function tryHandleWlDemoPost(method, relPathWithQuery, data) {
  const m = (method || "get").toLowerCase();
  if (m !== "post") {
    return null;
  }
  const [pathOnly] = relPathWithQuery.split("?");
  const p = pathOnly.replace(/\/+$/, "");
  const payload = normalizePayload(data);

  if (p.includes("validate-ean-upc")) {
    const eanUpc = payload.ean_upc ?? payload.ean ?? "";
    const excludeReleaseId = payload.exclude_release_id ?? payload.release_id ?? null;
    return { status: 200, data: validateDemoEanUpc(eanUpc, excludeReleaseId) };
  }

  if (p === "labels/validate-label-name" || p === "labels/validate-label-name/") {
    return {
      status: 200,
      data: validateLabelName(payload.label_name, payload.exclude_id),
    };
  }

  if (p.includes("validate-urlparts")) {
    const payload = normalizePayload(data);
    return {
      status: 200,
      data: validateMusicLinkUrlParts(payload.url_part1, payload.url_part2),
    };
  }

  if (p === "dropbox-upload" || p === "dropbox-upload/") {
    return { status: 200, data: {} };
  }

  if (p === "generate-2fa-backup-codes" || p === "generate-2fa-backup-codes/") {
    return { status: 200, data: { backup_codes: generateDemoBackupCodes() } };
  }

  if (p === "sub-users/validate-email" || p === "sub-users/validate-email/") {
    return { status: 200, data: validateSubUserEmail(payload?.email) };
  }

  if (p === "sub-users/validate-username" || p === "sub-users/validate-username/") {
    return { status: 200, data: validateSubUserUsername(payload?.username) };
  }

  if (p === "sub-users" || p === "sub-users/") {
    try {
      return { status: 201, data: handleCreateSubUser(payload || {}) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not create sub-user." },
      };
    }
  }

  if (p === "artists/get-or-create-artist" || p === "artists/get-or-create-artist/") {
    return { status: 200, data: getOrCreateArtist(payload || {}) };
  }

  if (p === "artists" || p === "artists/") {
    return { status: 201, data: appendArtist(payload || {}) };
  }

  if (p === "artist-wishlist" || p === "artist-wishlist/") {
    return { status: 201, data: appendArtistWishlist(payload || {}) };
  }

  if (p === "releases" || p === "releases/") {
    return { status: 201, data: appendRelease(payload || {}) };
  }

  if (p === "labels" || p === "labels/") {
    return { status: 201, data: appendLabel(payload || {}) };
  }

  if (p === "musiclinks" || p === "musiclinks/") {
    return { status: 201, data: appendMusicLink(payload || {}) };
  }

  if (p === "musiclinks_urlparts" || p === "musiclinks_urlparts/") {
    return { status: 201, data: appendMusicLinkUrlPart(payload || {}) };
  }

  if (p === "link-landingpages" || p === "link-landingpages/") {
    return { status: 201, data: appendLanding(payload || {}) };
  }

  if (p === "contributors" || p === "contributors/") {
    return { status: 201, data: appendContributor(payload || {}) };
  }

  if (p === "publishers" || p === "publishers/") {
    return { status: 201, data: appendPublisher(payload || {}) };
  }

  if (p === "shareholders" || p === "shareholders/") {
    return { status: 201, data: appendShareholder(payload || {}) };
  }

  if (p === "track-costs" || p === "track-costs/") {
    return { status: 201, data: appendTrackCost(payload || {}) };
  }

  if (p === "releases/perform-pre-scan" || p === "releases/perform-pre-scan/") {
    try {
      const releaseId = payload.release_id ?? payload.releaseId;
      if (!releaseExistsForDemoPreScan(releaseId)) {
        return { status: 404, data: { detail: "Release not found." } };
      }
      return { status: 200, data: handlePerformDemoPreScan(releaseId) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not perform pre-scan." },
      };
    }
  }

  if (p === "release-acr-deep-scan" || p === "release-acr-deep-scan/") {
    try {
      return {
        status: 200,
        data: handleDemoPreScanDeclaration(payload.id, payload.admin_declaration),
      };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not submit pre-scan declaration." },
      };
    }
  }

  if (p === "release-acr-scan" || p === "release-acr-scan/") {
    try {
      return { status: 200, data: handleDemoAcrScanDeclaration(payload) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not submit copyright declaration." },
      };
    }
  }

  if (p === "tenant-branding-update" || p === "tenant-branding-update/") {
    return { status: 200, data: mergeTenantFromBranding(payload || {}) };
  }

  if (p === "tenant-stripe-config" || p === "tenant-stripe-config/") {
    try {
      return { status: 200, data: saveDemoStripeConfig(payload || {}) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not save Stripe configuration." },
      };
    }
  }

  if (p === "tenant-stripe-config/sync-webhook" || p === "tenant-stripe-config/sync-webhook/") {
    try {
      return { status: 200, data: syncDemoStripeWebhook() };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not sync webhook." },
      };
    }
  }

  if (p === "tenant-stripe-products" || p === "tenant-stripe-products/") {
    try {
      return { status: 201, data: createDemoStripeProduct(payload || {}) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not create product." },
      };
    }
  }

  const checkoutMatch = p.match(/^tenant-stripe-products\/(\d+)\/checkout-session$/);
  if (checkoutMatch) {
    try {
      return {
        status: 200,
        data: createDemoCheckoutSession(checkoutMatch[1], payload || {}),
      };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not start checkout." },
      };
    }
  }

  if (
    p === "invoices/generate-invoice" ||
    p === "invoices/generate-invoice/"
  ) {
    try {
      return { status: 201, data: handleGenerateDemoInvoice() };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "No statement available for invoicing." },
      };
    }
  }

  if (
    p === "subuser-invoices/generate-invoice" ||
    p === "subuser-invoices/generate-invoice/"
  ) {
    try {
      return { status: 201, data: handleGenerateDemoSubuserInvoice() };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "No statement available for invoicing." },
      };
    }
  }

  const queueSubuserReportMatch = p.match(
    /^statements\/(\d+)\/queue-subuser-report$/,
  );
  if (queueSubuserReportMatch) {
    try {
      return {
        status: 200,
        data: handleQueueSubuserReport(queueSubuserReportMatch[1]),
      };
    } catch (err) {
      return {
        status: 400,
        data: {
          detail: err?.message || "Could not queue subuser report generation.",
        },
      };
    }
  }

  if (
    p === "subuser-invoices/update-invoice-status" ||
    p === "subuser-invoices/update-invoice-status/"
  ) {
    try {
      return { status: 200, data: handleUpdateSubuserInvoiceStatus(payload) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not update invoice status." },
      };
    }
  }

  if (p === "tenant-invoice-config" || p === "tenant-invoice-config/") {
    try {
      return { status: 200, data: handleUpdateTenantInvoiceConfig(payload) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not save invoice configuration." },
      };
    }
  }

  if (p === "tax-rates" || p === "tax-rates/") {
    try {
      return { status: 201, data: handleCreateTaxRate(payload) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not create tax rate." },
      };
    }
  }

  if (p === "subuser-statements/update-status" || p === "subuser-statements/update-status/") {
    try {
      return { status: 200, data: handleUpdateSubuserStatementStatus(payload) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not update statement status." },
      };
    }
  }

  if (
    p === "subuser-statements/update-visibility" ||
    p === "subuser-statements/update-visibility/"
  ) {
    try {
      return { status: 200, data: handleUpdateSubuserStatementVisibility(payload) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not update statement visibility." },
      };
    }
  }

  if (
    p === "subuser-statements/bulk-make-visible" ||
    p === "subuser-statements/bulk-make-visible/"
  ) {
    try {
      return { status: 200, data: handleBulkMarkSubuserStatementsVisible(payload) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not update statement visibility." },
      };
    }
  }

  if (
    p === "subuser-statements/bulk-send-email" ||
    p === "subuser-statements/bulk-send-email/"
  ) {
    try {
      return { status: 200, data: handleBulkSendSubuserStatementEmails(payload) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not send statement emails." },
      };
    }
  }

  return null;
}

/** PATCH/PUT artists/:id/ for browser-session demo (updates sessionStorage overlay). */
export function tryHandleWlDemoPatch(method, relPathWithQuery, data) {
  if (!(isBrowserSessionDemo() || isDemoMode())) {
    return null;
  }
  const m = (method || "").toLowerCase();
  if (m !== "patch" && m !== "put") {
    return null;
  }
  const [pathOnly] = relPathWithQuery.split("?");
  const p = pathOnly.replace(/\/+$/, "");
  const segments = p.split("/").filter(Boolean);
  if (segments[0] === "artists" && segments.length >= 2) {
    const id = segments[1];
    if (!id || id === "spotify-artist-details" || !/^\d+$/.test(String(id))) {
      return null;
    }
    const row = patchArtistRecord(id, normalizePayload(data) || {});
    return { status: 200, data: row };
  }

  if (segments[0] === "releases" && segments.length >= 2) {
    const id = segments[1];
    if (!id || !/^\d+$/.test(String(id))) {
      return null;
    }
    const row = patchReleaseRecord(id, normalizePayload(data) || {});
    return { status: 200, data: row };
  }

  if (segments[0] === "labels" && segments.length >= 2) {
    const id = segments[1];
    if (!id || !/^\d+$/.test(String(id))) {
      return null;
    }
    const row = patchLabelRecord(id, normalizePayload(data) || {});
    return { status: 200, data: row };
  }

  if (segments[0] === "link-landingpages" && segments.length >= 2) {
    const id = segments[1];
    if (!id || !/^\d+$/.test(String(id))) {
      return null;
    }
    const row = patchLandingRecord(id, normalizePayload(data) || {});
    return { status: 200, data: row };
  }

  if (segments[0] === "musiclinks" && segments.length >= 2) {
    const id = segments[1];
    if (!id || !/^\d+$/.test(String(id))) {
      return null;
    }
    const row = patchMusicLinkRecord(id, normalizePayload(data) || {});
    return { status: 200, data: row };
  }

  if (segments[0] === "musiclinks_urlparts" && segments.length >= 2) {
    const id = segments[1];
    if (!id || !/^\d+$/.test(String(id))) {
      return null;
    }
    const row = patchMusicLinkUrlPartRecord(id, normalizePayload(data) || {});
    return { status: 200, data: row };
  }

  if (segments[0] === "tracks" && segments.length >= 2) {
    const id = segments[1];
    if (!id || !/^\d+$/.test(String(id))) {
      return null;
    }

    const sid = String(id);
    const details = readJson(STORAGE.releaseDetails, {});
    const mergedDetails = getReleaseDetailsMerged();
    let updatedTrack = null;

    for (let i = 0; i < mergedDetails.length; i += 1) {
      const detail = mergedDetails[i];
      const tracks = Array.isArray(detail?.tracks) ? detail.tracks : [];
      const idx = tracks.findIndex(track => String(track.id) === sid);
      if (idx < 0) {
        continue;
      }

      const nextTracks = [...tracks];
      nextTracks[idx] = { ...nextTracks[idx], ...(normalizePayload(data) || {}) };
      updatedTrack = nextTracks[idx];

      const detailId = String(detail.id);
      const allTracksReady =
        nextTracks.length > 0 && nextTracks.every(track => !!track.track_data_complete);
      const nextDetail = { ...detail, tracks: nextTracks, all_tracks_ready: allTracksReady };
      details[detailId] = nextDetail;
      writeJson(STORAGE.releaseDetails, details);

      const releaseExtras = readJson(STORAGE.releases, []);
      const listExtraIndex = releaseExtras.findIndex(row => String(row.id) === detailId);
      if (listExtraIndex >= 0) {
        releaseExtras[listExtraIndex] = {
          ...releaseExtras[listExtraIndex],
          all_tracks_ready: allTracksReady,
        };
        writeJson(STORAGE.releases, releaseExtras);
      }
      break;
    }

    if (!updatedTrack) {
      // In demo mode we still want PATCH /tracks/:id to succeed even if that id
      // is not present in the in-memory/session release details snapshot.
      return {
        status: 200,
        data: {
          id: Number(id) || id,
          ...(normalizePayload(data) || {}),
        },
      };
    }

    return { status: 200, data: updatedTrack };
  }

  if (segments[0] === "sub-users" && segments.length >= 2) {
    const id = segments[1];
    if (!id || !/^\d+$/.test(String(id))) {
      return null;
    }
    try {
      const payload = normalizePayload(data) || {};
      const row =
        m === "put"
          ? handleUpdateSubUser(id, payload)
          : handlePatchSubUser(id, payload);
      return { status: 200, data: row };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not update sub-user." },
      };
    }
  }

  if (segments[0] === "tenant-stripe-products" && segments.length >= 2) {
    const id = segments[1];
    if (!id || !/^\d+$/.test(String(id))) {
      return null;
    }
    try {
      const row = updateDemoStripeProduct(id, normalizePayload(data) || {});
      return { status: 200, data: row };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not update product." },
      };
    }
  }

  if (segments[0] === "tax-rates" && segments.length >= 2) {
    const id = segments[1];
    if (!id || !/^\d+$/.test(String(id))) {
      return null;
    }
    try {
      const row = handleUpdateTaxRate(id, normalizePayload(data) || {});
      return { status: 200, data: row };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not update tax rate." },
      };
    }
  }

  if (segments[0] === "users" && segments.length >= 2) {
    const id = segments[1];
    if (!id || !/^\d+$/.test(String(id))) {
      return null;
    }
    const payload = normalizePayload(data) || {};
    const user = getMergedDemoUser();
    if (String(user.id) !== String(id)) {
      return { status: 404, data: { detail: "Not found." } };
    }
    if (payload.two_factor_method != null) {
      return { status: 200, data: patchDemoUserTwoFactor({ two_factor_method: payload.two_factor_method }) };
    }
    return { status: 200, data: patchDemoUserTwoFactor(payload) };
  }

  return null;
}

/** DELETE artists/:id/ for browser-session demo. */
export function tryHandleWlDemoDelete(method, relPathWithQuery) {
  if (!(isBrowserSessionDemo() || isDemoMode())) {
    return null;
  }
  if ((method || "").toLowerCase() !== "delete") {
    return null;
  }
  const [pathOnly] = relPathWithQuery.split("?");
  const p = pathOnly.replace(/\/+$/, "");
  const segments = p.split("/").filter(Boolean);
  if (!["artists", "artist-wishlist", "link-landingpages", "musiclinks", "musiclinks_urlparts", "tax-rates", "tenant-stripe-products"].includes(segments[0]) || segments.length < 2) {
    return null;
  }
  const id = segments[1];
  if (!id || !/^\d+$/.test(String(id))) {
    return null;
  }
  if (segments[0] === "artists") {
    removeArtistRecord(id);
  } else if (segments[0] === "artist-wishlist") {
    removeArtistWishlist(id);
  } else if (segments[0] === "musiclinks") {
    removeDemoMusicLinkRow(id);
  } else if (segments[0] === "musiclinks_urlparts") {
    removeDemoMusicLinkUrlPartRow(id);
  } else if (segments[0] === "tenant-stripe-products") {
    try {
      return { status: 200, data: deleteDemoStripeProduct(id) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not delete product." },
      };
    }
  } else if (segments[0] === "tax-rates") {
    try {
      return { status: 200, data: handleDeleteTaxRate(id) };
    } catch (err) {
      return {
        status: 400,
        data: { detail: err?.message || "Could not delete tax rate." },
      };
    }
  } else {
    removeDemoLandingRow(id);
  }
  return { status: 204, data: {} };
}
